import { useState } from 'react';

import { BehavioralInsightPresentationMode, EventTypes } from 'modules/domains/segmentation';
import { pxToRem } from 'utils/convert';
import { getKeyByValue } from '@iterable/utils';
import { BaseOption } from 'ui/selects';
import { SegmentedControlOption } from 'ui/segmentedControl';
import { ITERABLE_EVENTS, HUMAN_READABLE_ITERABLE_EVENTS } from 'constants/eventTypes';

import { InsightSelect } from './InsightSelect';
import { Trigger, TriggerWrapper } from './InsightSelectTrigger';
import { useCustomBehavioralContext } from './contexts';
import { useTranslation } from '../hooks';
import { getEventInfo } from './utils';

const selectMediumOptions: SegmentedControlOption[] = [
  { name: 'Show total count', value: BehavioralInsightPresentationMode.TotalCount },
  { name: 'Show per user', value: BehavioralInsightPresentationMode.Rate },
];

export const BEHAVIORAL_EVENT_DATA_TEST = {
  select: 'behavioral-event-select',
  toggle: 'behavioral-event-toggle',
};

const getEventSuffix = (dataType: EventTypes) => {
  const custom = 'custom';
  const system = 'system';
  return dataType === EventTypes.CustomEvent ? custom : system;
};

const behavioralEventSelectWidth = pxToRem(350);

export const BehavioralEventSelect = () => {
  const { t } = useTranslation();
  const {
    addCustomBehavioralEvent,
    selectableEventsForProject,
    cachedCustomBehavioralInsights,
    maxBehavioralInsightSettingsForProject,
  } = useCustomBehavioralContext();
  /** we want TotalCount to be the default */
  const [isTotalCount, updateMode] = useState<boolean>(true);
  const presentationMode = isTotalCount
    ? BehavioralInsightPresentationMode.TotalCount
    : BehavioralInsightPresentationMode.Rate;
  const toggleMode = (mode: BehavioralInsightPresentationMode) => {
    if (mode !== presentationMode) {
      updateMode((previous) => !previous);
    }
  };

  const options = selectableEventsForProject.map<BaseOption<string>>(({ event, dataType }) => ({
    id: `behavior-event-${event}-${dataType}`,
    label: getEventInfo(dataType, HUMAN_READABLE_ITERABLE_EVENTS, event),
    value: event,
    Suffix: getEventSuffix(dataType),
  }));

  const searchPlaceholder = t('insights.select.filterPlaceholder');
  const placeholder = t('insights.select.behavioralEventPlaceholder');
  const isMaxPropertiesSelected =
    !!cachedCustomBehavioralInsights &&
    cachedCustomBehavioralInsights.length === maxBehavioralInsightSettingsForProject;

  return (
    <InsightSelect
      isDisabled={isMaxPropertiesSelected}
      selectTestId={BEHAVIORAL_EVENT_DATA_TEST.select}
      selectPlaceholder={placeholder}
      selectOptions={options}
      onOptionSelect={([value]) => {
        const event = `${value}`;
        const dataType = getKeyByValue(ITERABLE_EVENTS, event, EventTypes.CustomEvent);
        const eventName = getEventInfo(dataType, HUMAN_READABLE_ITERABLE_EVENTS, event);
        addCustomBehavioralEvent(eventName, presentationMode, dataType);
      }}
      showSegmentedControl
      segmentedControlTestId={BEHAVIORAL_EVENT_DATA_TEST.toggle}
      segmentedControlMode={presentationMode}
      segmentedControlOptions={selectMediumOptions}
      onSegmentedControlSelect={toggleMode}
      textInputPlaceholder={searchPlaceholder}
      Trigger={Trigger}
      SelectWrapper={TriggerWrapper}
      width={behavioralEventSelectWidth}
    />
  );
};
